import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BaseSiteService, LanguageService } from '@spartacus/core';
import { NavigationNode } from '@spartacus/storefront';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';

import { DpProductSubcategoriesApiService } from '@app/core/services/api/product-subcategories-api.service';
import { ProductSubcategoriesDto } from '@app/core/types/product-subcategories.dto';

@Injectable({ providedIn: 'root' })
export class DpNavigationCategoriesService {
  readonly #baseSiteService = inject(BaseSiteService);
  readonly #languageService = inject(LanguageService);
  readonly #apiService = inject(DpProductSubcategoriesApiService);
  readonly #destroyRef = inject(DestroyRef);

  getNavigationProductCategories(): Observable<NavigationNode[]> {
    return this.#getProductCatalog$().pipe(
      switchMap((productCatalog) => this.#apiService.getSubcategoriesById(productCatalog)),
      map((category: ProductSubcategoriesDto) => this.#mapProductCategoriesIntoNavigationNodes(category.subcategories))
    );
  }

  #getProductCatalog$(): Observable<string> {
    return combineLatest([
      this.#baseSiteService.getActive().pipe(distinctUntilChanged()),
      this.#languageService.getActive()
    ]).pipe(
      takeUntilDestroyed(this.#destroyRef),
      switchMap(([activeId]) => this.#baseSiteService.get(activeId).pipe(take(1))),
      filter(Boolean),
      map((activeSite) => activeSite?.productCatalog?.id || '')
    );
  }

  #mapProductCategoriesIntoNavigationNodes(categories: ProductSubcategoriesDto[] = []): NavigationNode[] {
    return categories.map((category) => ({
      title: category.name,
      url: `c/${category.id}`,
      children: this.#mapProductCategoriesIntoNavigationNodes(category.subcategories)
    }));
  }
}
